import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { PlacesType, Tooltip as ReactTooltip } from "react-tooltip";

interface Props {
  id: string;
  content: string;
  children: ReactNode;
  className?: string;
  place?: PlacesType;
  tooltipClasses?: string;
  contentMode?: "html" | "text";
  openOnClick?: boolean;
  closeOnEsc?: boolean;
  clickable?: boolean;
  closeOnClickOutside?: boolean;
}

const DEFAULT_STYLES = {
  color: "#fff",
  borderRadius: "8px",
  fontSize: "12px",
};

export const WithTooltip: FC<Props> = ({
  id,
  content,
  className,
  children,
  place = "top",
  tooltipClasses,
  contentMode = "html",
  openOnClick = false,
  closeOnEsc = false,
  clickable = false,
  closeOnClickOutside = false,
}) => {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const contentAttributes =
    contentMode === "text" ? { "data-tooltip-content": content } : { "data-tooltip-html": content };

  useEffect(() => {
    if (!closeOnClickOutside) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      const tooltip = document.getElementById(id);

      if (!(target instanceof Node) || anchorRef.current?.contains(target) || tooltip?.contains(target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => document.removeEventListener("click", handleClickOutside, true);
  }, [closeOnClickOutside, id]);

  return (
    <>
      <div ref={anchorRef} className={className} data-tooltip-id={id} {...contentAttributes}>
        {children}
      </div>
      <ReactTooltip
        id={id}
        place={place}
        style={DEFAULT_STYLES}
        // Disabled by CSP
        disableStyleInjection={true}
        className={tooltipClasses}
        openOnClick={openOnClick}
        closeOnEsc={closeOnEsc}
        clickable={clickable}
        globalCloseEvents={closeOnClickOutside ? { escape: closeOnEsc } : undefined}
        isOpen={closeOnClickOutside ? isOpen : undefined}
        setIsOpen={closeOnClickOutside ? setIsOpen : undefined}
      />
    </>
  );
};

export default WithTooltip;
